import { z } from "zod";
export const quantityFormDraftSchema = z.object({
  kind: z.literal("quantity"), recordType: z.enum(["PRODUCTION", "WASTE", "SCRAP"]),
  quantity: z.string().max(40), unit: z.string().max(80), factor: z.number().positive().finite().nullable(),
  unitObservedAt: z.string().nullable(), primaryUnit: z.string().max(80),
  reasonCodeId: z.string().max(100), note: z.string().max(500),
  scrapDisposition: z.enum(["ANIMAL_FEED", "REUSABLE", ""]),
}).strict();
export const reasonFormDraftSchema = z.object({
  kind: z.literal("downtime"), reasonCodeId: z.string().max(100),
  customMode: z.boolean().default(false),
  customReason: z.string().max(160).default(""),
  note: z.string().max(500),
}).strict();
export const operatorDraftDataSchema = z.discriminatedUnion("kind", [quantityFormDraftSchema, reasonFormDraftSchema]);
export const operatorDraftSchema = z.object({ key: z.string(), intentId: z.string().uuid(), revision: z.number().int().positive(),
  data: operatorDraftDataSchema }).strict();
export type OperatorDraftData = z.infer<typeof operatorDraftDataSchema>;
export type OperatorDraftRecord = z.infer<typeof operatorDraftSchema>;
export type QuantityFormDraft = z.infer<typeof quantityFormDraftSchema>;

export type OperatorDraftScope = {
  terminalCode: string; operatorId: string; sessionId: string; lineId: string; runId: string; form: string;
};
const scopeSchema = z.tuple([z.string(), z.string(), z.string(), z.string(), z.string(), z.string()]);
const legacyScopeSchema = z.tuple([z.string(), z.string(), z.string(), z.string(), z.string()]);

/** The line is part of the scope so a terminal reassignment never reveals a draft on another line. */
export function operatorDraftKey(terminalCode: string, operatorId: string, sessionId: string, runId: string, form: string) {
  return JSON.stringify([terminalCode, operatorId, sessionId, runId, form]);
}
export function operatorDowntimeDraftKey(terminalCode: string, operatorId: string, sessionId: string, lineId: string, runId: string, form: string) {
  return JSON.stringify([terminalCode, operatorId, sessionId, lineId, runId, form]);
}
export function operatorDraftScopeFromKey(key: string): OperatorDraftScope | null {
  try {
    const parsed = scopeSchema.safeParse(JSON.parse(key));
    if (!parsed.success) return null;
    const [terminalCode, operatorId, sessionId, lineId, runId, form] = parsed.data;
    return { terminalCode, operatorId, sessionId, lineId, runId, form };
  } catch { return null; }
}
function legacyScopeFromKey(key: string) {
  try {
    const parsed = legacyScopeSchema.safeParse(JSON.parse(key));
    if (!parsed.success) return null;
    const [terminalCode, operatorId, sessionId, runId, form] = parsed.data;
    return { terminalCode, operatorId, sessionId, runId, form };
  } catch { return null; }
}
export function recoverableOperatorDraft(records: unknown[], target: OperatorDraftScope): OperatorDraftRecord | null {
  const valid = records.flatMap((value) => {
    const record = operatorDraftSchema.safeParse(value);
    return record.success && record.data.data.kind === "downtime" ? [record.data] : [];
  });
  const exact = valid.find((record) => record.key === operatorDowntimeDraftKey(target.terminalCode, target.operatorId, target.sessionId, target.lineId, target.runId, target.form));
  if (exact) return exact;
  // A previous app version lacked a line id. It is only eligible in exactly the same session.
  const sameSessionLegacy = valid.filter((record) => {
    const scope = legacyScopeFromKey(record.key);
    return scope != null && scope.terminalCode === target.terminalCode && scope.operatorId === target.operatorId &&
      scope.sessionId === target.sessionId && scope.runId === target.runId && scope.form === target.form;
  });
  if (sameSessionLegacy.length === 1) return sameSessionLegacy[0];
  const forward = valid.filter((record) => {
    const scope = operatorDraftScopeFromKey(record.key);
    return scope != null && scope.sessionId !== target.sessionId && scope.terminalCode === target.terminalCode &&
      scope.operatorId === target.operatorId && scope.lineId === target.lineId && scope.runId === target.runId && scope.form === target.form;
  });
  return forward.length === 1 && forward[0].data.kind === "downtime" ? forward[0] : null;
}
